import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {trpc, createMutationErrorHandler} from '@common-web/lib/trpc';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {Textarea} from '@common-web/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {toast} from '@common-web/hooks/use-toast';
import {SCOPE_LABELS, SCOPE_OPTIONS} from '@common/permissions';
import {ArrowLeft, Save, UserCog} from 'lucide-react';
import type {RoleScope} from '@internal/components/roles-permissions/types';

export function RoleNew() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState<RoleScope>('CLIENT');
  const [clientId, setClientId] = useState('');

  // Custom roles are tenant-owned: non-INTERNAL roles require an owning
  // client and are only visible/assignable within that tenant.
  const needsTenant = scope !== 'INTERNAL';
  const {data: clients, isLoading: clientsLoading} = trpc.internal.client.list.useQuery(undefined, {
    enabled: needsTenant,
  });

  const createMutation = trpc.internal.roles.create.useMutation({
    onSuccess: async (created) => {
      await utils.internal.roles.list.invalidate();
      toast({title: 'Role created'});
      navigate(`/roles/${created.id}`);
    },
    onError: createMutationErrorHandler('Failed to create role'),
  });

  const handleSubmit = () => {
    createMutation.mutate({
      key: key.trim(),
      name: name.trim(),
      description: description.trim() ? description.trim() : undefined,
      scope,
      clientId: needsTenant ? clientId : undefined,
    });
  };

  const isPending = createMutation.isPending;

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='max-w-3xl mx-auto p-8 space-y-4'>
        <Button variant='ghost' onClick={() => navigate('/permissions?tab=roles')}>
          <ArrowLeft className='h-4 w-4 mr-2' />
          Back to roles
        </Button>

        <Card className='border-gray-200 shadow-sm'>
          <CardHeader className='border-b bg-gray-50'>
            <CardTitle className='text-2xl font-bold text-brand-navy flex items-center gap-2'>
              <UserCog className='h-5 w-5' />
              New role
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-5 pt-6'>
            <p className='text-sm text-brand-gray'>
              Create a new role and assign permissions to it on the next screen.
            </p>

            <div className='grid gap-1.5'>
              <Label htmlFor='role-key'>Key</Label>
              <Input
                id='role-key'
                value={key}
                onChange={(e) => setKey(e.target.value.toLowerCase())}
                placeholder='client:reporting'
                disabled={isPending}
                className='font-mono'
              />
              <p className='text-xs text-brand-gray'>
                A unique identifier for this role. Use lowercase letters and colons, e.g.{' '}
                <code className='font-mono bg-gray-100 px-1 rounded'>client:reporting</code>.
              </p>
            </div>

            <div className='grid gap-1.5'>
              <Label htmlFor='role-name'>Display name</Label>
              <Input
                id='role-name'
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder='Client Reporting Analyst'
                disabled={isPending}
              />
            </div>

            <div className='grid gap-1.5'>
              <Label htmlFor='role-description'>Description</Label>
              <Textarea
                id='role-description'
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                disabled={isPending}
                placeholder='Describe who this role is for and what it grants.'
              />
            </div>

            <div className='grid gap-1.5'>
              <Label htmlFor='role-scope'>Who is this role for?</Label>
              <Select
                value={scope}
                onValueChange={(value) => setScope(value as RoleScope)}
                disabled={isPending}
              >
                <SelectTrigger id='role-scope' className='w-64'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCOPE_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {SCOPE_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className='text-xs text-brand-gray'>
                Determines which types of users can be assigned this role and which permissions it
                can include.
              </p>
            </div>

            {needsTenant && (
              <div className='grid gap-1.5'>
                <Label htmlFor='role-client'>Client</Label>
                <Select
                  value={clientId}
                  onValueChange={setClientId}
                  disabled={isPending || clientsLoading}
                >
                  <SelectTrigger id='role-client' className='w-64'>
                    <SelectValue placeholder={clientsLoading ? 'Loading…' : 'Select a client'} />
                  </SelectTrigger>
                  <SelectContent>
                    {(clients ?? []).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className='text-xs text-brand-gray'>
                  Custom roles belong to one client and can only be assigned to that client's
                  users. Internal-portal roles have no client.
                </p>
              </div>
            )}

            <div className='flex items-center justify-end pt-4 border-t'>
              <Button
                onClick={handleSubmit}
                disabled={isPending || !key || !name || (needsTenant && !clientId)}
              >
                <Save className='h-4 w-4 mr-2' />
                Create role
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
